<!-- eslint-disable max-len -->
<!--
*  TTTech nerve-ui-components
*  Copyright(c) 2021. TTTech Industrial Automation AG.
*
*  ALL RIGHTS RESERVED.
*
*  Usage of this software, including source code, netlists, documentation,
*  is subject to restrictions and conditions of the applicable license
*  agreement with TTTech Industrial Automation AG or its affiliates.
*
*  All trademarks used are the property of their respective owners.
*
*  TTTech Industrial Automation AG and its affiliates do not assume any liability
*  arising out of the application or use of any product described or shown
*  herein. TTTech Industrial Automation AG and its affiliates reserve the right to
*  make changes, at any time, in order to improve reliability, function or
*  design.
*
*  Contact Information:
*  support@tttech-industrial.com
*
*  TTTech Industrial Automation AG, Schoenbrunnerstrasse 7, 1040 Vienna, Austria
*
* -->
<template>
  <div class="workload pl-3">
    <v-row v-resize="onResize" class="mt-10">
      <v-icon
        id="iiotWorkloadControlGoBackBtn"
        data-cy="iiotWorkloadControlGoBackBtn"
        large
        class="ml-n9 mr-4 mt-1"
        @click="$emit('go-back')"
        >mdi-chevron-left
      </v-icon>
      <v-img
        v-if="['started', 'suspended', 'stopped', 'removing_failed'].indexOf(workloadModel.currentStatus) !== -1"
        :id="`iiotWorkloadControlDockerStatus_${workloadModel.currentStatus}`"
        :src="`/img/${workloadModel.currentStatus}.svg`"
        :lazy-src="`/img/${workloadModel.currentStatus}.png`"
        max-height="10px"
        max-width="10px"
        class="status-position ml-n3"
      />
      <v-progress-circular
        v-else
        id="iiotWorkloadControlDockerCircularProgress"
        indeterminate
        color="primary"
        size="15"
        width="2"
        class="progress-position ml-n4"
      />
      <div
        :id="`iiotWorkloadControlLink-${isVersionClickable}`"
        class="d-flex"
        :class="{ 'pointer link': isVersionClickable }"
        @click="linkClicked()"
      >
        <div
          :style="{
            color: isVersionClickable && isErrorMessage !== 'red' ? 'primary' : isErrorMessage,
          }"
          class="status-message"
        >
          <h1 id="iiotWorkloadControlWorklaodName" class="title-weight">
            {{
              $t('nodes.deployedWorkloadControl.statusMessageTitle', {
                name: workloadModel.name,
              })
            }}
          </h1>
        </div>
        <v-tooltip v-if="workloadModel && workloadModel.version && !isSatisfied" top mr-6>
          <template #activator="{ on, attrs }">
            <span v-bind="attrs" id="iiotWorkloadControlVersionName" class="positionOfVersion" v-on="on">
              [{{ workloadModel.version }}]
            </span>
          </template>
          <span>{{ $t('nodes.deployedWorkloadControl.releaseName') }}</span>
        </v-tooltip>
        <span
          v-if="workloadModel && workloadModel.version && isSatisfied"
          id="iiotWorkloadControlVersionName"
          class="positionOfVersion"
        >
          [{{ workloadModel.version }}]
        </span>
      </div>
    </v-row>
    <v-row :class="{ 'mt-2': !isLocalUi }" class="ml-2">
      <span v-if="workloadModel && workloadModel.versionsAvailableForWorkload" data-cy="iiotWorkloadControlNodeName">
        <span class="node-title"> {{ $t('nodes.deployedWorkloadControl.node') }}: </span>
        <span class="node-status">{{ nodeModel.name }}</span>
      </span>
    </v-row>

    <v-col
      :class="{
        'mt-5 mb-10': workloadModel.type === 'docker' || workloadModel.type === 'docker-compose',
      }"
      class="pl-0"
    >
      <v-row>
        <v-col>
          <v-tabs
            v-model="tab"
            :class="{
              'tabs-border': workloadModel.type !== 'codesys' || !isLocalUi,
              'mb-n11': workloadModel.type === 'codesys' && isLocalUi,
            }"
            next-icon="mdi-arrow-right-bold-box-outline"
            prev-icon="mdi-arrow-left-bold-box-outline"
            show-arrows
          >
            <v-tab
              id="iiotWorkloadControlTab"
              :key="control"
              :class="{
                'd-none': workloadModel.type === 'codesys' && isLocalUi,
              }"
              data-cy="iiotWorkloadControlTab"
              @click="tabChanged(0)"
            >
              <h4>
                {{ $t('nodes.deployedWorkloadControl.control') }}
              </h4>
            </v-tab>

            <v-tab
              v-if="(workloadModel.type === 'docker' || workloadModel.type === 'docker-compose') && isSatisfied"
              id="iiotWorkloadControlDetailsTab"
              :key="details"
              data-cy="iiotWorkloadControlDetailsTab"
              @click="tabChanged(1)"
            >
              <h4>{{ $t('nodes.deployedWorkloadControl.details') }}</h4>
            </v-tab>

            <v-tab
              v-if="workloadModel.type === 'vm' && permissions.uiWorkloadVmBackup"
              id="iiotWorkloadControlBackupTab"
              :key="backup"
              data-cy="iiotWorkloadControlBackupTab"
              @click="tabChanged(2)"
            >
              <h4>{{ $t('nodes.deployedWorkloadControl.backup') }}</h4>
            </v-tab>

            <v-tab
              v-if="workloadModel.type === 'vm' && permissions.uiWorkloadSnapshot"
              id="iiotWorkloadControlSnapshotTab"
              :key="snapshot"
              data-cy="iiotWorkloadControlSnapshotTab"
              @click="tabChanged(3)"
            >
              <h4>{{ $t('nodes.deployedWorkloadControl.snapshot') }}</h4>
            </v-tab>
            <v-tab
              v-if="workloadModel.type === 'docker' && isLocalUi"
              id="iiotWorkloadControlLogsTab"
              :key="logs"
              data-cy="iiotWorkloadControlLogsTab"
              @click="tabChanged(4)"
            >
              <h4>
                {{ $t('nodes.deployedWorkloadControl.logs') }}
              </h4>
            </v-tab>
            <v-tab
              v-if="!isLocalUi && permissions.uiWorkloadControlControl"
              id="iiotWorkloadControlActivityLogsTab"
              :key="activityLogsTab"
              data-cy="iiotWorkloadControlActivityLogsTab"
              @click="tabChanged(5, 'activityLogsTab')"
            >
              <h4>{{ $t('nodes.activityLogs.activityLogs') }}</h4>
            </v-tab>
          </v-tabs>
        </v-col>
      </v-row>
      <v-tabs-items v-model="tab">
        <v-tab-item :key="control">
          <div class="main-box mt-8">
            <v-row>
              <v-col cols="12" lg="4" class="pa-0 pt-2 pr-12 bcg">
                <v-row class="pb-4 pt-4">
                  <div>
                    <div elevation="1" min-width="400">
                      <v-row class="d-flex align-center">
                        <v-col cols="3" class="pa-0 pr-12">
                          <v-avatar
                            :id="`iiotWorkloadControlAvatar-${workloadModel.type}`"
                            :class="[
                              'workload-control-avatar-size-and-color-' + `${workloadModel.type}`,
                              {
                                fading:
                                  workloadModel.currentStatus === 'creating' ||
                                  workloadModel.currentStatus === 'removing' ||
                                  workloadModel.currentStatus === 'suspending' ||
                                  workloadModel.currentStatus === 'starting' ||
                                  workloadModel.currentStatus === 'restarting' ||
                                  workloadModel.currentStatus === 'resuming' ||
                                  workloadModel.currentStatus === 'stopping',
                              },
                            ]"
                            :data-cy="`iiotWorkloadControlAvatar-${workloadModel.type}`"
                            size="60"
                          >
                            <v-img
                              v-if="workloadModel.type === 'docker-compose'"
                              :id="`iiotWorkloadControlAvatarImage-${workloadModel.type}`"
                              :src="`/img/${workloadModel.type}.svg`"
                              :data-cy="`iiotWorkloadControlAvatarImage-${workloadModel.type}`"
                            />
                            <v-icon v-else class="deployed-workload-icon ma-3">
                              {{ `$vuetify.icons.${workloadModel.type}` }}
                            </v-icon>
                          </v-avatar>
                        </v-col>
                        <v-col
                          :class="[
                            {
                              'pl-10': workloadModel.type === 'codesys' && !isLocalUi,
                            },
                            {
                              'pl-5': workloadModel.type === 'vm' && !isLocalUi,
                            },
                            {
                              'pl-5': workloadModel.type == 'docker' || workloadModel.type === 'docker-compose',
                            },
                            {
                              'pl-7': workloadModel.type === 'vm' && isLocalUi,
                            },
                          ]"
                          cols="9"
                        >
                          <v-row
                            :class="[
                              { 'pl-6': workloadModel.type === 'codesys' },
                              { 'pl-4': workloadModel.type !== 'codesys' },
                            ]"
                          >
                            <v-col
                              v-for="ctrl in controls"
                              :key="ctrl.command"
                              :class="[
                                {
                                  'pl-2 pr-2': workloadModel.type === 'codesys',
                                },
                                {
                                  'pl-1 pr-1': workloadModel.type === 'vm' && !isLocalUi,
                                },
                                {
                                  'pl-1 pr-1':
                                    (workloadModel.type === 'docker' || workloadModel.type === 'docker-compose') &&
                                    !isLocalUi,
                                },
                                {
                                  'pl-2':
                                    (workloadModel.type === 'docker' || workloadModel.type === 'docker-compose') &&
                                    isLocalUi,
                                },
                              ]"
                              class="pa-0 pt-1"
                              cols="3"
                            >
                              <v-tooltip bottom>
                                <template #activator="{ on }">
                                  <div v-on="on">
                                    <v-btn
                                      :id="'iiotVmControls' + ctrl.name"
                                      :ref="'iiotVmControls' + ctrl.name"
                                      :data-cy="'iiotVmControls' + ctrl.name"
                                      :disabled="!isEnabledBtn(ctrl) || !permissions.uiWorkloadControlControl"
                                      class="button-control-wc"
                                      color="tttech-blue-button"
                                      @click="workloadCommand(ctrl)"
                                    >
                                      <v-icon>
                                        {{ ctrl.icon }}
                                      </v-icon>
                                    </v-btn>
                                  </div>
                                </template>
                                <span>{{ $t(`nodes.deployedWorkloadControl.${ctrl.name}`) }}</span>
                              </v-tooltip>
                            </v-col>
                          </v-row>
                        </v-col>
                      </v-row>
                      <v-col class="font-weight-light ml-7 font-15 mt-7">
                        <v-row>
                          <span :style="{ color: isErrorMessage }" data-cy="iiotWorkloadControlStatusMessageOfWorkload">
                            {{
                              $t('nodes.deployedWorkloadControl.statusMessage', {
                                name: workloadModel.name,
                                status:
                                  $t(`nodes.information.devices.status.${workloadModel.currentStatus}`) || 'error',
                              })
                            }}
                          </span>
                        </v-row>
                        <v-row class="mt-4">
                          <time-in-words v-if="workloadModel.timestamp" :date="workloadModel.timestamp" class="mb-2" />
                        </v-row>
                      </v-col>
                    </div>
                  </div>
                </v-row>
                <v-divider class="mr-5 mt-3" />
                <v-row>
                  <v-col class="pl-9 pb-10 pt-6" lg="9" md="12" sm="12">
                    <v-row class="mt-2">
                      <vm-resources
                        v-if="workloadModel.type === 'vm' && workloadModel.resources"
                        :id="'wlControlVmResource'"
                        :input-cpu="workloadModel.resources.cpu"
                        :input-memory="workloadModel.resources.memory"
                        :input-device-id="workloadModel.id"
                        :edit-resources="permissions.uiWorkloadEditResources"
                        @save="saveChanges"
                      />
                    </v-row>
                    <v-row>
                      <update-deployed-workload-btn
                        v-if="workloadModel.type !== 'vm' && updateBtnEnabling.enabled"
                        :versions-of-workload="updateBtnEnabling.numberOfVersionsAvailable"
                        :is-local-ui="isLocalUi"
                        @update-btn-clicked="updateDeployedWorkload"
                      />
                    </v-row>
                    <v-row>
                      <nerve-button
                        v-if="canApplyConfiguration"
                        id="iiotWorkloadControlApplyConfigurationBtn"
                        ref="iiotWorkloadControlApplyConfigurationBtn"
                        :text="$t('nodes.deployedWorkloadControl.applyWorkloadConfigurationBtn')"
                        type-of-btn="action"
                        class="mt-3 mb-0"
                        size="normal"
                        data-cy="iiotWorkloadControlApplyConfigurationBtn"
                        @click-event="openApplyConfigurationDialog"
                      />
                    </v-row>
                    <v-col
                      v-if="showWorkloadConfigurationStatusSection"
                      id="iiotWorkloadControlWorkloadConfigurationSection"
                      ref="iiotWorkloadControlWorkloadConfigurationSection"
                    >
                      <v-row class="font-15 mt-6">
                        {{ $t('nodes.deployedWorkloadControl.workloadConfigurationMessageTitle') }}
                      </v-row>
                      <v-row class="text-lowercase font-15">
                        <time-in-words
                          v-if="workloadModel.configurationUpdateStatus.timestamp"
                          :date="workloadModel.configurationUpdateStatus.timestamp"
                        />
                      </v-row>
                      <v-row>
                        <v-col :class="{ 'mt-3': !isLocalUi }">
                          <v-row :style="{ color: isErrorMessage }">
                            <span class="node-title mr-1">
                              {{ $t('nodes.deployedWorkloadControl.workloadConfigurationMessageContainerPath') }}
                            </span>
                            <span class="node-status">
                              {{ workloadModel.configurationUpdateStatus.containerPath }}
                            </span>
                          </v-row>
                          <v-row
                            :style="{ color: isErrorMessage }"
                            :class="[{ 'mt-5': !isLocalUi }, { 'mt-2': isLocalUi }]"
                          >
                            <span class="node-title mr-1">
                              {{ $t('nodes.deployedWorkloadControl.workloadConfigurationMessageVolumeName') }}
                            </span>
                            <span class="node-status">
                              {{ workloadModel.configurationUpdateStatus.volumeName }}
                            </span>
                          </v-row>
                          <v-row
                            :style="{ color: isErrorMessage }"
                            :class="[{ 'mt-5': !isLocalUi }, { 'mt-2': isLocalUi }]"
                          >
                            <span class="node-title mr-1">
                              {{ $t('nodes.deployedWorkloadControl.workloadConfigurationMessageStatus') }}
                            </span>
                            <span class="node-status">
                              {{
                                $t(
                                  `nodes.deployedWorkloadControl.workloadConfigurationStatus${
                                    workloadModel.configurationUpdateStatus.status || 'NoStatus'
                                  }`,
                                )
                              }}
                            </span>
                          </v-row>
                          <v-row
                            :style="{ color: isErrorMessage }"
                            :class="[{ 'mt-5': !isLocalUi }, { 'mt-2': isLocalUi }]"
                          >
                            <span class="node-title mr-1">
                              {{ $t('nodes.deployedWorkloadControl.workloadConfigurationMessageInitiatedBy') }}
                            </span>
                            <span class="node-status">
                              {{ workloadModel.configurationUpdateStatus.user }}
                            </span>
                          </v-row>
                          <v-row
                            v-if="workloadModel.configurationUpdateStatus.status === 'FAILED'"
                            id="iiotWorkloadControlWorkloadConfigurationMessage"
                            ref="iiotWorkloadControlWorkloadConfigurationMessage"
                            :style="{ color: isErrorMessage }"
                          >
                            <span class="node-title mr-1">
                              {{ $t('nodes.deployedWorkloadControl.workloadConfigurationMessageContent') }}
                            </span>
                            <span class="node-status">
                              {{ workloadModel.configurationUpdateStatus.message }}
                            </span>
                          </v-row>
                        </v-col>
                      </v-row>
                    </v-col>
                  </v-col>
                </v-row>
              </v-col>
              <v-col :class="{ 'pl-9': !isStatusBarDividerVisible }" cols="12" lg="8" xl="8">
                <v-divider v-if="isStatusBarDividerVisible" class="status-bar-divider" />
                <v-col v-if="workloadModel.type !== 'codesys'" class="pb-10 pl-0" cols="12" xl="6" lg="6" md="6" sm="6">
                  <div>
                    <status-bar
                      v-if="workloadModel.type !== 'codesys'"
                      :progress-percent="workloadModel.stats.cpu"
                      :status-name="$t('nodes.deployedWorkloadControl.progress.cpu')"
                    />
                  </div>
                  <div class="ram-monitoring-position">
                    <status-bar
                      v-if="workloadModel.type === 'docker' || workloadModel.type === 'docker-compose'"
                      :progress-percent="workloadModel.stats.ram"
                      :status-name="$t('nodes.deployedWorkloadControl.progress.ram')"
                    />
                  </div>
                </v-col>
                <v-divider
                  v-if="isConnectBtnEnabled && workloadModel.type !== 'codesys'"
                  :class="[
                    {
                      'status-divider-do': workloadModel.type === 'docker' || workloadModel.type === 'docker-compose',
                    },
                    { 'status-divider-vm': workloadModel.type === 'vm' },
                  ]"
                />
                <div v-if="isConnectBtnEnabled" class="mb-10 max-table-width">
                  <div
                    :class="{
                      'mt-5': workloadModel.type !== 'codesys',
                      'mt-0': workloadModel.type === 'codesys',
                    }"
                    class="mb-5"
                  >
                    <span class="font-18">
                      {{ $t('nodes.deployedWorkloadControl.connections') }}
                    </span>
                  </div>
                  <v-data-table
                    :headers="headers"
                    :items="remoteConnections"
                    hide-default-footer
                    disable-pagination
                    dense
                    class="elevation-1 max-table-height"
                  >
                    <template #[`item.action`]="{ item }">
                      <v-btn
                        :id="'iiotWorkloadControlConnectBtn' + item.name"
                        :disabled="!isWorkloadOnline() || !permissions.uiRemoteConnConnect"
                        :data-cy="'iiotWorkloadControlConnectBtn' + item.name"
                        text
                        color="primary"
                        class="ml-n1 pa-0 no-uppercase"
                        @click="connectingBtnClicked(item)"
                      >
                        {{ $t('globals.connect') }}
                      </v-btn>
                    </template>
                  </v-data-table>
                </div>
                <v-divider
                  v-if="workloadModel.type === 'vm' && permissions.uiWorkloadNetworkInterfaceDetails"
                  :class="[
                    {
                      'status-divider-do': workloadModel.type === 'docker' || workloadModel.type === 'docker-compose',
                    },
                    {
                      'status-divider-vm': workloadModel.type === 'vm' && !isConnectBtnEnabled,
                    },
                    {
                      'mt-15': workloadModel.type === 'vm' && !isConnectBtnEnabled && isLocalUi,
                    },
                  ]"
                />
                <div v-if="workloadModel.type === 'vm' && permissions.uiWorkloadNetworkInterfaceDetails">
                  <network-properties
                    :params="networkParams"
                    :error-message="errorMessageNetwork"
                    :store-module="networkStoreModule"
                    :custom-fetch-action="customFetchActionNetwork"
                    :custom-count-geter="customCountGetterNetwork"
                    :custom-list-getter="customListGetterNetwork"
                    :vm-status="workloadModel.currentStatus"
                  />
                </div>
              </v-col>
            </v-row>
          </div>
        </v-tab-item>
        <v-tab-item
          v-if="(workloadModel.type === 'docker' || workloadModel.type === 'docker-compose') && isSatisfied"
          :key="details"
        >
          <div class="mt-8">
            <v-row>
              <v-col :class="{ 'pr-10': !isStatusBarDividerVisible }" cols="12" lg="6" xl="6">
                <v-row class="ml-0">
                  <span class="font-18">
                    {{ $t('nodes.deployedWorkloadControl.environmentVariables.title') }}
                  </span>
                </v-row>
                <div
                  :class="{
                    'pl-0': workloadModel.environmentVariables && workloadModel.environmentVariables.length === 0,
                    'pl-2': workloadModel.environmentVariables && workloadModel.environmentVariables.length !== 0,
                  }"
                  class="pr-2 pt-10 pb-5"
                >
                  <v-data-table
                    id="iiotWorkloadContorlDetailsEnvironmentVariablesTable"
                    :class="{
                      'elevation-1':
                        workloadModel.environmentVariables && workloadModel.environmentVariables.length !== 0,
                    }"
                    :headers="headersEnvironmentVariables"
                    :items="workloadModel.environmentVariables"
                    :no-data-text="$t('nodes.deployedWorkloadControl.environmentVariables.noEnvironmentVariables')"
                    :hide-default-header="
                      workloadModel.environmentVariables && workloadModel.environmentVariables.length === 0
                    "
                    hide-default-footer
                    disable-pagination
                    dense
                    disable-sort
                    data-cy="iiotWorkloadContorlDetailsEnvironmentVariablesTable"
                    class="details-table"
                  />
                  <p
                    v-if="
                      workloadModel.type === 'docker-compose' &&
                      workloadModel.environmentVariables &&
                      workloadModel.environmentVariables.length !== 0
                    "
                    id="iiotWorkloadControlDetailsEnvironmentVariablesMessage"
                    class="environmentVariables pt-7"
                  >
                    {{ $t('nodes.deployedWorkloadControl.environmentVariables.environmentVariablesMessage') }}
                  </p>
                </div>
              </v-col>
              <v-divider v-if="!isStatusBarDividerVisible" vertical class="status-bar-divider" />
              <v-col :class="{ 'pl-10': !isStatusBarDividerVisible }" cols="12" lg="6" xl="6">
                <v-row class="ml-0">
                  <span class="font-18">
                    {{ $t('nodes.deployedWorkloadControl.portMappings.title') }}
                  </span>
                </v-row>
                <div
                  :class="{
                    'pl-0': workloadModel.ports && workloadModel.ports.length === 0,
                    'pl-2': workloadModel.ports && workloadModel.ports.length !== 0,
                  }"
                  class="pr-2 pt-10 pb-5"
                >
                  <v-data-table
                    id="iiotWorkloadContorlDetailsPortsMappingsTable"
                    :class="{
                      'elevation-1': workloadModel.ports && workloadModel.ports.length !== 0,
                    }"
                    :headers="headersPortMappings"
                    :items="workloadModel.ports"
                    :no-data-text="$t('nodes.deployedWorkloadControl.portMappings.noPortMappings')"
                    :hide-default-header="workloadModel.ports && workloadModel.ports.length === 0"
                    hide-default-footer
                    disable-pagination
                    dense
                    disable-sort
                    data-cy="iiotWorkloadContorlDetailsPortsMappingsTable"
                    class="details-table"
                  />
                </div>
              </v-col>
            </v-row>
          </div>
        </v-tab-item>

        <v-tab-item v-if="workloadModel.type === 'vm' && permissions.uiWorkloadVmBackup" :key="backup">
          <div class="mt-8 ml-4">
            <vm-backups
              :params="backupParams"
              :store-module="backupStoreModule"
              :backup-model="backupModel"
              :is-backup-add-disabled="isBackupAddDisabled"
              :error-message-vm-backup="errorMessageVmBackup"
              :workload-id="workloadModel.workloadId"
              :version-id="workloadModel.versionId"
              :edit-backups="permissions.uiWorkloadVmEditBackup"
              @create-vm-backup="(item) => $emit('create-vm-backup', item)"
            />
          </div>
        </v-tab-item>
        <v-tab-item v-if="workloadModel.type === 'vm' && permissions.uiWorkloadSnapshot" :key="snapshot">
          <div class="mt-8 ml-4">
            <vm-snapshot
              :params="snapshotParams"
              :store-module="snapshotStoreModule"
              :saved-schedule="schedule"
              :workload-id="workloadModel.workloadId"
              :version-id="workloadModel.versionId"
              :edit-snapshots="permissions.uiWorkloadEditSnapshot"
              @saveSchedule="(item) => $emit('save-schedule', item)"
              @deleteSchedule="(item) => $emit('delete-schedule', item)"
              @restoreSnapshot="(item) => $emit('restore-snapshot', item)"
              @saveSnapshot="(item) => $emit('save-snapshot', item)"
            />
          </div>
        </v-tab-item>
        <v-tab-item v-if="workloadModel.type === 'docker' && isLocalUi" :key="logs">
          <v-container fluid>
            <nerve-workload-logs
              :logs="workloadModel.logs"
              :loading="logsLoading"
              :disabled="logsActionDisabled"
              @freeze-logs="(freeze) => $emit('freeze-logs', freeze)"
              @download-logs="$emit('download-logs')"
              @copy-logs="(logs) => $emit('copy-logs', logs)"
            ></nerve-workload-logs>
          </v-container>
        </v-tab-item>
        <v-tab-item v-if="!isLocalUi && permissions.uiWorkloadControlControl" :key="activityLogsTab">
          <div>
            <activity-logs
              ref="activityLog"
              :activity-logs="activityLogs"
              :loading="loading"
              :columns-info="columnsInfo"
              :limit-per-page="limitPerPage"
              :audit-log-permission="permissions.uiAuditLogs"
              :count="count"
              @download-activity-logs="$emit('download-activity-logs')"
              @load-more-activity-logs="(params) => $emit('load-more-activity-logs', params)"
              @view-activity-logs-for-selected-workload="$emit('view-activity-logs-for-selected-workload')"
            >
            </activity-logs>
          </div>
        </v-tab-item>
      </v-tabs-items>
    </v-col>
  </div>
</template>

<script>
import TimeInWords from './shared/TimeInWords.vue';
import StatusBar from './state-bar/StatusBar.vue';
import VmResources from './VmResources.vue';
import UpdateDeployedWorkloadBtn from './workload/updateWorkloadBtn.vue';
import shared from './helpers/shared';
import NerveButton from './NerveButton.vue';
import NetworkProperties from './NetworkProperties.vue';
import VmBackups from './VmBackups.vue';
import VmSnapshot from './VmSnapshot.vue';
import NerveWorkloadLogs from './workload/NerveWorkloadLogs.vue';
import ActivityLogs from './ActivityLogs.vue';

const COMMANDS = {
  DEPLOY: 'DEPLOY',
  UNDEPLOY: 'UNDEPLOY',
  STOP: 'STOP',
  START: 'START',
  SUSPEND: 'SUSPEND',
  RESUME: 'RESUME',
  RESTART: 'RESTART',
  UNINSTALL: 'UNINSTALL',
  DOWNLOAD: 'DOWNLOAD',
};
const STATUSES = {
  NO_CODESYS: -1,
  IDLE: 0,
  CREATING: 1,
  REMOVING: 2,
  SUSPENDING: 3,
  SUSPENDED: 4,
  STARTING: 5,
  RESTARTING: 6,
  RESUMING: 7,
  STARTED: 8,
  STOPPING: 9,
  STOPPED: 10,
  ERROR: 11,
  REMOVING_FAILED: 12,
};
/**
 * Array of predefined controls
 * @type {Array}
 */
const CONTROLS = [
  {
    command: COMMANDS.START,
    name: 'Start',
    icon: 'mdi-play',
    enableFor: [STATUSES.STOPPED],
    visibleFor: [['all'], [STATUSES.STOPPED, STATUSES.STARTING, STATUSES.RESUMING]],
    updateTo: STATUSES.STARTING, // this will be used for forced status updates
  },
  {
    command: COMMANDS.RESUME,
    name: 'Play',
    icon: 'mdi-play',
    enableFor: [STATUSES.SUSPENDED],
    visibleFor: [['vm', 'docker', 'docker-compose'], [STATUSES.SUSPENDED]],
    updateTo: STATUSES.RESUMING,
  },
  {
    command: COMMANDS.SUSPEND,
    name: 'Pause',
    icon: 'mdi-pause',
    enableFor: [STATUSES.STARTED],
    visibleFor: [
      ['vm', 'docker', 'docker-compose'],
      [STATUSES.STARTED, STATUSES.SUSPENDING, STATUSES.STOPPING, STATUSES.RESTARTING],
    ],
    updateTo: STATUSES.SUSPENDING,
  },
  {
    command: COMMANDS.STOP,
    name: 'Stop',
    icon: 'mdi-stop',
    enableFor: [STATUSES.STARTED],
    visibleFor: [['all'], ['all']],
    updateTo: STATUSES.STOPPING,
  },
  {
    command: COMMANDS.RESTART,
    name: 'Restart',
    icon: 'mdi-restart',
    enableFor: [STATUSES.STARTED],
    visibleFor: [['vm', 'docker', 'docker-compose'], ['all']],
    updateTo: STATUSES.RESTARTING,
  },
  {
    command: COMMANDS.UNDEPLOY,
    icon: 'mdi-delete',
    name: 'Undeploy',
    enableFor: [
      STATUSES.STARTED,
      STATUSES.STOPPED,
      STATUSES.SUSPENDED,
      STATUSES.STARTING,
      STATUSES.STOPPING,
      STATUSES.RESUMING,
      STATUSES.SUSPENDING,
      STATUSES.RESTARTING,
      STATUSES.ERROR,
      STATUSES.REMOVING_FAILED,
      STATUSES.CREATING,
    ],
    visibleFor: [['vm', 'docker', 'codesys', 'docker-compose'], ['all']],
    updateTo: STATUSES.REMOVING,
  },
];

const CONTROLS_CODESYS = [
  {
    command: COMMANDS.START,
    name: 'Start',
    icon: 'mdi-play',
    enableFor: [STATUSES.STOPPED],
    visibleFor: [['all'], [STATUSES.STOPPED, STATUSES.STARTING]],
    updateTo: STATUSES.STARTING, // this will be used for forced status updates
  },
  {
    command: COMMANDS.STOP,
    name: 'Stop',
    icon: 'mdi-stop',
    enableFor: [STATUSES.STARTED],
    visibleFor: [['all'], [STATUSES.STOPPING, STATUSES.STARTED]],
    updateTo: STATUSES.STOPPING,
  },
  {
    command: COMMANDS.DOWNLOAD,
    name: 'Download',
    icon: 'download',
    enableFor: [
      STATUSES.STARTED,
      STATUSES.STOPPED,
      STATUSES.SUSPENDED,
      STATUSES.STARTING,
      STATUSES.STOPPING,
      STATUSES.RESUMING,
      STATUSES.SUSPENDING,
      STATUSES.RESTARTING,
      STATUSES.ERROR,
      STATUSES.REMOVING_FAILED,
    ],
    visibleFor: [['codesys'], ['all']],
  },
  {
    command: COMMANDS.UNDEPLOY,
    icon: 'mdi-delete',
    name: 'Undeploy',
    enableFor: [
      STATUSES.STARTED,
      STATUSES.STOPPED,
      STATUSES.SUSPENDED,
      STATUSES.STARTING,
      STATUSES.STOPPING,
      STATUSES.RESUMING,
      STATUSES.SUSPENDING,
      STATUSES.RESTARTING,
      STATUSES.ERROR,
      STATUSES.REMOVING_FAILED,
      STATUSES.CREATING,
    ],
    visibleFor: [['vm', 'docker', 'codesys', 'docker-compose'], ['all']],
    updateTo: STATUSES.REMOVING,
  },
];

export default {
  name: 'WorkloadControl',
  components: {
    TimeInWords,
    StatusBar,
    VmResources,
    UpdateDeployedWorkloadBtn,
    NerveButton,
    NetworkProperties,
    VmBackups,
    VmSnapshot,
    NerveWorkloadLogs,
    ActivityLogs,
  },
  props: {
    nodeModel: {
      type: Object,
      default: () => ({
        name: undefined,
        serialNumber: undefined,
        id: undefined,
        version: undefined,
      }),
    },
    workloadModel: {
      type: Object,
      default: () => {},
    },
    remoteConnections: {
      type: Array,
      default: () => {},
    },
    permissions: {
      type: Object,
      default: () => ({
        uiWorkloadConfigurationApply: false,
        uiWorkloadControlControl: false,
        uiRemoteConnConnect: false,
        uiWorkloadNetworkInterfaceDetails: false,
        uiWorkloadSnapshot: false,
        uiWorkloadVmBackup: false,
        uiAuditLogs: false,
        uiWorkloadEditSnapshot: true,
        uiWorkloadVmEditBackup: true,
        uiWorkloadEditResources: true,
      }),
    },
    codesysControlEnabled: {
      type: Boolean,
      default: false,
    },
    isConnectBtnEnabled: {
      type: Boolean,
      default: false,
    },
    updateBtnEnabling: {
      type: Object,
      default: () => ({}),
    },
    isSnapshotBtnDisabled: {
      type: Boolean,
      default: true,
    },
    isLocalUi: {
      type: Boolean,
      default: true,
    },
    networkParams: {
      type: Object,
      default: () => {},
    },
    errorMessageNetwork: {
      type: String,
      default: '',
    },
    errorMessageVmBackup: {
      type: String,
      default: '',
    },
    networkStoreModule: {
      type: String,
      default: '',
    },
    customFetchActionNetwork: {
      type: String,
      default: '',
    },
    customCountGetterNetwork: {
      type: String,
      default: '',
    },
    customListGetterNetwork: {
      type: String,
      default: '',
    },
    backupStoreModule: {
      type: String,
      default: '',
    },
    backupParams: {
      type: Object,
      default: () => {},
    },
    backupModel: {
      type: Object,
      default: () => ({
        nodeName: undefined,
        nodeSerialNumber: '',
        vmName: undefined,
        vmCurrentStatus: undefined,
      }),
    },
    isBackupAddDisabled: {
      type: Boolean,
      default: false,
    },
    isBackupDisabled: {
      type: Boolean,
      default: true,
    },
    snapshotParams: {
      type: Object,
      default: () => {},
    },
    snapshotStoreModule: {
      type: String,
      default: '',
    },
    schedule: {
      type: Object,
      default: () => {},
    },
    vmStatus: {
      type: String,
      default: '',
    },
    logsLoading: {
      type: Boolean,
      default: () => false,
    },
    logsActionDisabled: {
      type: Boolean,
      default: () => false,
    },
    isVersionClickable: {
      type: Boolean,
      default: false,
    },
    activityLogs: {
      type: Array,
      default: () => {},
    },
    loading: {
      type: Boolean,
      default: () => false,
    },
    columnsInfo: {
      type: Array,
      default: () => [],
    },
    limitPerPage: {
      type: Number,
      default: () => 50,
    },
    count: {
      type: Number,
      default: () => 50,
    },
  },

  data() {
    return {
      tab: null,
      control: 0,
      details: 1,
      backup: 2,
      snapshot: 3,
      logs: 4,
      activityLogsTab: 5,
      isStatusBarDividerVisible: true,
    };
  },
  computed: {
    headers() {
      return [
        {
          text: this.$t('nodes.deployedWorkloadControl.name'),
          value: 'name',
          width: '200px',
          sortable: false,
        },
        {
          text: this.$t('nodes.deployedWorkloadControl.type'),
          value: 'type',
          width: '50px',
          sortable: false,
        },
        {
          text: this.$t('nodes.deployedWorkloadControl.port'),
          value: 'port',
          width: '50px',
          sortable: false,
        },
        {
          text: this.$t('nodes.deployedWorkloadControl.approval'),
          value: 'acknowledgment',
          width: '50px',
          sortable: false,
        },
        {
          text: this.$t('nodes.deployedWorkloadControl.action'),
          value: 'action',
          width: '50px',
          sortable: false,
        },
      ];
    },
    headersEnvironmentVariables() {
      return [
        {
          text: this.$t('nodes.deployedWorkloadControl.environmentVariables.variable'),
          value: 'variable',
        },
        {
          text: this.$t('nodes.deployedWorkloadControl.environmentVariables.value'),
          value: 'value',
        },
      ];
    },
    headersPortMappings() {
      return [
        {
          text: this.$t('nodes.deployedWorkloadControl.portMappings.protocol'),
          value: 'protocol',
        },
        {
          text: this.$t('nodes.deployedWorkloadControl.portMappings.hostPort'),
          value: 'hostPort',
        },
        {
          text: this.$t('nodes.deployedWorkloadControl.portMappings.containerPort'),
          value: 'containerPort',
        },
      ];
    },
    // flag that indicates can a user control codesys workloads (start/stop workload) or not
    isCodesysControlEnabled() {
      return !this.codesysControlEnabled && this.workloadModel.type === 'codesys';
    },
    isUndeployBtnDisabled() {
      return this.workloadModel.currentStatus === STATUSES.REMOVING;
    },
    controls() {
      if (this.workloadModel.type === 'codesys') {
        return CONTROLS_CODESYS.filter((c) => this.isVisibleBtn(c));
      }
      return CONTROLS.filter((c) => this.isVisibleBtn(c));
    },
    showWorkloadConfigurationStatusSection() {
      return (
        (this.workloadModel.type === 'docker' || this.workloadModel.type === 'docker-compose') &&
        this.workloadModel.configurationUpdateStatus &&
        this.workloadModel.configurationUpdateStatus !== {} &&
        this.workloadModel.configurationUpdateStatus.status
      );
    },
    canApplyConfiguration() {
      return (
        (this.workloadModel.type === 'docker' || this.workloadModel.type === 'docker-compose') &&
        this.permissions.uiWorkloadConfigurationApply &&
        this.workloadModel.configurationUpdateInfo &&
        this.workloadModel.configurationUpdateInfo.configurationVolumes &&
        this.workloadModel.configurationUpdateInfo.configurationVolumes.length > 0 &&
        STATUSES[this.workloadModel.currentStatus.toUpperCase()] !== STATUSES.REMOVING &&
        STATUSES[this.workloadModel.currentStatus.toUpperCase()] !== STATUSES.REMOVING_FAILED
      );
    },
    isErrorMessage() {
      if (this.workloadModel.currentStatus === undefined) {
        return 'red';
      }
      return this.workloadModel &&
        this.workloadModel.currentStatus &&
        STATUSES[this.workloadModel.currentStatus.toUpperCase()] === STATUSES.ERROR
        ? 'red'
        : 'black';
    },
    isSatisfied() {
      if (this.isLocalUi) {
        return true;
      }
      if (!this.nodeModel.version) {
        return false;
      }
      return shared.isNoPrerelesedVersionGreaterThanOrEqualTo(this.nodeModel.version, '2.5.0');
    },
  },
  methods: {
    undeployBtnClicked() {
      const commandForUndeploy = CONTROLS.find((c) => c.command === COMMANDS.UNDEPLOY);
      this.$emit('undeploy-btn-clicked', commandForUndeploy);
    },
    async connectingBtnClicked(rc) {
      this.$emit('connecting-btn-clicked', rc);
    },
    async workloadCommand(command) {
      if (command.command === 'UNDEPLOY') {
        this.undeployBtnClicked();
        return;
      }
      const params = {
        command: command.command,
        serialNumber: this.nodeModel.serialNumber,
        deviceId: this.workloadModel.id,
      };
      this.$emit('workload-command', { params, command });
    },
    isEnabledBtn(ctrl) {
      const status = this.workloadModel.currentStatus ? this.workloadModel.currentStatus.toUpperCase() : '';
      return this.workloadModel && (ctrl.enableFor.includes(STATUSES[status]) || ctrl.enableFor[0] === 'all');
    },
    isVisibleBtn(btn) {
      if (!this.isLocalUi && this.workloadModel.type === 'codesys' && btn.command !== 'UNDEPLOY') {
        return;
      }

      const status = this.workloadModel.currentStatus ? this.workloadModel.currentStatus.toUpperCase() : '';
      // eslint-disable-next-line consistent-return
      return (
        this.workloadModel &&
        this.isVisibleForStatus(btn, STATUSES[status]) &&
        this.isVisibleForType(btn, this.workloadModel.type)
      );
    },
    isVisibleForStatus(btn, status) {
      return btn.visibleFor[1].includes(status) || btn.visibleFor[1][0] === 'all';
    },
    isVisibleForType(btn, type) {
      return btn.visibleFor[0].includes(type) || btn.visibleFor[0][0] === 'all';
    },
    isWorkloadOnline() {
      const status = this.workloadModel.currentStatus ? this.workloadModel.currentStatus.toUpperCase() : '';
      return STATUSES[status] === STATUSES.STARTED;
    },
    isInTransitionalStatus() {
      const status = this.workloadModel.currentStatus ? this.workloadModel.currentStatus.toUpperCase() : '';
      return [
        STATUSES.STARTING,
        STATUSES.STOPPING,
        STATUSES.RESUMING,
        STATUSES.RESTARTING,
        STATUSES.REMOVING,
        STATUSES.CREATING,
        STATUSES.SUSPENDING,
      ].includes(STATUSES[status]);
    },
    openApplyConfigurationDialog() {
      this.$emit('open-apply-configuration-dialog');
    },
    saveChanges(data) {
      this.$emit('update-vm-resource-allocation', data);
    },
    updateDeployedWorkload() {
      this.$emit('update-deployed-workload');
    },
    downloadCodesysAppArchive() {
      this.$emit('download-codesys-app-archive');
    },
    backupBtnClicked() {
      this.$emit('backup-btn-clicked');
    },
    async tabChanged(tab, tabName) {
      this.$emit('tab-changed', { workloadId: this.workloadModel.id, tab });
      if (tabName === 'activityLogsTab') {
        this.$emit('load-more-activity-logs', { page: 1, limit: this.limitPerPage, search: '' });
        this?.$refs?.activityLog?.resetNumberOfPage();
      }
    },
    onResize() {
      this.isStatusBarDividerVisible = window.innerWidth < 1264;
    },
    linkClicked() {
      if (this.isVersionClickable) {
        this.$emit('reroute-user');
      }
    },
  },
};
</script>

<style lang="scss" >
.message-box-class-test {
  background: #6f42c1;
  width: 100%;
  height: 120px;
}
.first-row-class {
  margin-top: 30px;
}
.page-wrapper {
  padding: 51px 0 0 45px;
}
.tertiary-tttech-background-btn-workload-control {
  background-image: linear-gradient(to bottom, var(--v-tertiary-lighten4), var(--v-tertiary-base));
}
.primary-tttech-background-btn-workload-control {
  background-image: linear-gradient(to bottom, var(--v-primary-base), var(--v-primary-darken1));
}
.workload {
  .deployed-workload-icon {
    width: 40px !important;
    height: 40px !important;
  }

  @mixin workload-control-avatar-size-and-color($background) {
    margin-left: 40px;
    margin-top: 8px;
    width: 30px;
    height: 30px;
    max-width: 100px;
    max-height: 100px;
    background: $background;
  }
  .workload-control-avatar-size-and-color-vm {
    @include workload-control-avatar-size-and-color(linear-gradient(to top, #3810f2, #6f42c1));
  }

  .workload-control-avatar-size-and-color-codesys {
    @include workload-control-avatar-size-and-color(linear-gradient(to top, #dc3545, #e83e8c));
  }

  .workload-control-avatar-size-and-color-docker {
    @include workload-control-avatar-size-and-color(linear-gradient(to bottom, #ff9a00, #ff6100));
  }
  .workload-control-avatar-size-and-color-docker-compose {
    @include workload-control-avatar-size-and-color(none);
  }
  .control-button-box-wc {
    margin-right: 15px;
    margin-left: 15px;
    min-width: 250px;
    max-width: 400px;
    margin-top: 6px;
  }
  .primary-tttech-background-btn-workload-control {
    background-image: linear-gradient(to bottom, var(--v-primary-base), var(--v-primary-darken1));
  }
  .tttech-blue-button {
    color: var(--v-secondary-base) !important;
    @extend .primary-tttech-background-btn-workload-control;
  }
  .button-control-wc {
    border-radius: 50%;
    height: 40px !important;
    width: 40px !important;
    min-height: 20px !important;
    min-width: 20px !important;
    max-width: 50px !important;
    max-height: 50px !important;
    padding: 0;
  }
  .progress-bar-position {
    margin-top: -4px;
  }
  margin-left: 25px;
  .tttech-blue-button {
    color: var(--v-secondary-base) !important;
    @extend .primary-tttech-background-btn-workload-control;
  }

  .title-wc {
    margin-top: 20px;
    text-align: start;
    > h1 {
      text-align: left;
      font-size: 25px !important;
      font-weight: 800 !important;
      margin-top: 0;
      margin-bottom: 0;
    }

    > .v-divider {
      border-top-width: 3px !important;
      border-top-color: #00c5ea;
      width: 41px;
      margin: 0;
      margin-bottom: 15px;
    }
  }

  .status {
    font-size: 18px !important;
    font-weight: 700 !important;
  }

  .status-width {
    max-width: 300px;
  }

  .main-box {
    max-width: 1500px;
    @mixin icon-box($background) {
      background-image: $background;
      height: 88px;
      width: 88px;
      line-height: 0;
      max-height: 88px !important;
      max-width: 88px !important;
      border-radius: 50%;
      margin: -15px 0 -78px -7px;
      .image {
        width: 100%;
        height: 100%;
      }
    }

    .deployed-workload-icon {
      width: 50px;
      height: 50px;
      margin-bottom: 30px;
    }

    .font-weight-custom {
      text-transform: uppercase;
      font-size: 12px !important;
      margin-bottom: 13px;
    }

    .message-box {
      margin-bottom: 20px;
      background: #f0f3f5;
      text-transform: none;
      min-width: 350px;
      max-width: 500px;
      width: 383px;
      span {
        word-break: break-all;
        width: 100%;
        font-size: 15px;
      }
    }
    .message-box-for-docker-configuration {
      background: #f0f3f5;
      margin-right: 0px;
      text-transform: none;
      min-width: 350px;
      float: right;
      width: 100%;
      span {
        word-break: break-all;
        width: 100%;
        font-size: 15px;
      }
    }
  }

  @keyframes loader {
    0% {
      width: 0;
      left: -37%;
      right: 0;
    }
    50% {
      width: 141%;
      left: -37%;
      right: 0;
    }
    99% {
      width: 0;
      left: 105%;
      right: 0;
    }
  }

  .updatePosition {
    margin-top: 0px;
    margin-right: 5px;
  }

  .vm-resources-class {
    float: right;
  }
}
.ram-monitoring-position {
  margin-top: -25px;
}

.v-tab {
  text-transform: none !important;
}
.positionOfVersion {
  margin-top: 12px;
  margin-left: 5px;
  font-size: 13px;
}
.status-position {
  margin-right: 6px;
  margin-top: 16px;
}
.progress-position {
  margin-right: 5px;
  margin-top: 14px;
}
.bcg {
  border-right: 1px solid rgba(0, 0, 0, 0.05);
}
.node-title {
  font-size: 14px;
  color: rgba(0, 0, 0, 0.5);
}
.node-status {
  font-size: 14px;
  color: rgba(0, 0, 0, 0.75);
}
.title-weight {
  font-weight: normal;
  font-size: 25px;
}
.tabs-border {
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}
@keyframes fadeIn {
  from {
    opacity: 0;
  }
}
.fading {
  animation: fadeIn 1s infinite alternate;
}
.font-15 {
  font-size: 15px;
}
.font-18 {
  font-size: 18px;
}
.status-bar-divider {
  margin-top: 1px;
}
.status-divider-do {
  margin-top: 42px;
}
.status-divider-vm {
  margin-top: 80px;
}
.theme--light.v-divider {
  border-color: rgba(0, 0, 0, 0.05) !important;
}
.no-uppercase {
  text-transform: unset !important;
}
.max-table-width {
  max-width: 700px !important;
}
.max-table-height {
  max-height: 250px;
  overflow-y: auto;
}
.details-table {
  .v-data-table__wrapper {
    overflow-y: auto;
    max-height: 550px;
  }
}
.details-table > .v-data-table__wrapper > table > tbody > .v-data-table__empty-wrapper > td {
  text-align: start !important;
  padding-left: 0 !important;
}
.environmentVariables {
  color: rgba(0, 0, 0, 0.38);
}
.pointer {
  cursor: pointer !important;
}
.link {
  color: var(--v-primary-base);
}
.link:hover {
  border-bottom: 2px solid var(--v-primary-base);
}
</style>
